<template>
  <div v-if="isGateManagementEnabled">
    <div class="d-flex flex-column gap-4">
      <h4 class="mt-4">{{ t('warehouse.pages.settings.labels.checkIn') }}</h4>

      <p class="my-4">
        {{ t('warehouse.pages.settings.messages.selfCheckInCodeInstructions') }}
      </p>

      <div class="d-flex flex-row align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-open-in-new" size="20" class="mr-2" />
          {{ t('warehouse.pages.settings.labels.warehouseCheckInUrl') }}
        </div>
        <v-btn variant="text" @click="jumpToCheckIn()" class="bg-secondary ml-auto">
          {{ t('warehouse.pages.settings.buttons.checkInUrl') }}
        </v-btn>
      </div>

      <v-divider />

      <div class="d-flex flex-row align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-open-in-new" size="20" class="mr-2" />
          {{ t('warehouse.pages.settings.labels.checkInBanner') }}
        </div>
        <v-btn variant="outlined" @click="downloadCheckInTemplate" class="ml-auto">
          {{ t('global.basicTerms.print') }}
        </v-btn>
      </div>

      <v-divider />

      <div class="d-flex flex-row align-center justify-space-between my-4">
        <canvas id="checkin-qr"></canvas>
        <v-btn variant="outlined" @click="downloadCheckInQr()" class="ml-auto">
          {{ t('global.basicTerms.download') }}
        </v-btn>
      </div>

      <v-divider />
    </div>

    <div class="d-flex flex-column gap-4">
      <h4 class="mt-6">{{ t('warehouse.pages.settings.labels.checkOut') }}</h4>

      <p class="my-4">
        {{ t('warehouse.pages.settings.messages.selfCheckOutCodeInstructions') }}
      </p>

      <div class="d-flex flex-row align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-open-in-new" size="20" class="mr-2" />
          {{ t('warehouse.pages.settings.labels.warehouseCheckOutUrl') }}
        </div>
        <v-btn variant="text" @click="jumpToCheckOut()" class="bg-secondary ml-auto">
          {{ t('warehouse.pages.settings.buttons.checkOutUrl') }}
        </v-btn>
      </div>

      <v-divider />

      <div class="d-flex flex-row align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon icon="mdi-open-in-new" size="20" class="mr-2" />
          {{ t('warehouse.pages.settings.labels.checkOutBanner') }}
        </div>
        <v-btn variant="outlined" @click="downloadCheckOutTemplate" class="ml-auto">
          {{ t('global.basicTerms.print') }}
        </v-btn>
      </div>

      <v-divider />

      <div class="d-flex flex-row align-center justify-space-between my-4">
        <canvas id="checkout-qr"></canvas>
        <v-btn variant="outlined" @click="downloadCheckOutQr()" class="ml-auto">
          {{ t('global.basicTerms.download') }}
        </v-btn>
      </div>

      <v-divider />
    </div>

    <gate-management-template
      :warehouse-name="warehouse.name"
      action="check-in"
      id="printable-warehouse-template-check-in" />
    <gate-management-template
      :warehouse-name="warehouse.name"
      action="check-out"
      id="printable-warehouse-template-check-out" />
  </div>

  <div class="d-flex flex-row align-center" v-else>
    <p class="my-4 mr-4">
      <i18n-t keypath="warehouse.pages.settings.messages.gateManagementFeatureDescription">
        <template #salesLink>
          <a target="_blank" href="https://lp.opendock.com/gate-management">Contact Sales</a>
        </template>
      </i18n-t>
    </p>

    <v-btn color="secondary" @click="requestCheckInFeature">
      {{ t('warehouse.pages.settings.buttons.requestFeature') }}
    </v-btn>
  </div>
</template>

<script>
import { t } from '@nova/core';
import QRCode from 'qrcode';
import config from '@/config/config';
import { useAppointmentAnalytics } from '@/composables/useAppointmentAnalytics';

export default {
  name: 'GateManagementChecklist',
  props: {
    warehouse: {
      required: true,
      type: Object
    },
    isGateManagementEnabled: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return useAppointmentAnalytics();
  },
  computed: {
    checkInUrl() {
      return `${config.comet_url}/?warehouseId=${this.warehouse.id}`;
    },
    checkOutUrl() {
      return `${config.juno_url}/?warehouseId=${this.warehouse.id}`;
    }
  },
  methods: {
    t,
    jumpToCheckIn() {
      window.open(this.checkInUrl, '_blank');
    },
    downloadCheckInQr() {
      this.downloadQrCode(this.checkInUrl, `${this.warehouse.name}-checkin-qrcode.png`);
    },
    downloadCheckInTemplate() {
      this.downloadTemplate(this.checkInUrl, 'check-in');
    },
    jumpToCheckOut() {
      window.open(this.checkOutUrl, '_blank');
    },
    downloadCheckOutQr() {
      this.downloadQrCode(this.checkOutUrl, `${this.warehouse.name}-checkout-qrcode.png`);
    },
    downloadCheckOutTemplate() {
      this.downloadTemplate(this.checkOutUrl, 'check-out');
    },
    downloadQrCode(url, fileName) {
      QRCode.toDataURL(
        url,
        {
          errorCorrectionLevel: 'Q',
          width: 1000
        },
        (err, url) => {
          const linkEl = document.createElement('a');
          linkEl.href = url;
          linkEl.download = fileName;
          linkEl.click();
          linkEl.remove();
        }
      );
    },
    downloadTemplate(url, action) {
      QRCode.toDataURL(
        url,
        {
          errorCorrectionLevel: 'Q',
          width: 1000
        },
        (err, url) => {
          /**
           * Set generated qrcode inside the printable warehouse template
           */
          {
            const qrcodePlaceholder = document.getElementById(
              `qrcode-template-placeholder-${action}`
            );
            qrcodePlaceholder.innerHTML = '';

            const img = document.createElement('img');
            img.src = url;
            img.width = 370;
            img.height = 370;
            qrcodePlaceholder.appendChild(img);
          }

          /**
           * Create a new element for printing only the warehouse template
           */
          {
            const templateDomClone = document
              .getElementById(`printable-warehouse-template-${action}`)
              .cloneNode(true);

            const instructionsPrintable =
              document.getElementById('print-content') ?? document.createElement('section');
            instructionsPrintable.innerHTML = '';
            instructionsPrintable.id = 'print-content';
            instructionsPrintable.appendChild(templateDomClone);
            document.body.appendChild(instructionsPrintable);
          }
          /**
           * Wait 2 seconds before print the new content for the image to load
           */
          {
            this.notify('Warehouse template is being generated for printing...', 'info');
            setTimeout(() => window.print(), 2000);
          }
        }
      );
    },
    requestCheckInFeature() {
      this.trackSelfCheckInRequested({
        'Entry Point': 'Gate Management Settings',
        'Org Name': this.$org.name,
        'Org ID': this.$org.id,
        'Warehouse ID': this.warehouse.id,
        'Warehouse Name': this.warehouse.name
      });

      this.notify(
        this.t('warehouse.pages.settings.messages.yourRequestHasBeenSubmittedCSAWillContactYouSoon')
      );
    }
  },
  mounted() {
    this.$nextTick(() => {
      const checkinCanvas = document.getElementById('checkin-qr');
      const checkoutCanvas = document.getElementById('checkout-qr');

      QRCode.toCanvas(checkinCanvas, this.checkInUrl, {
        errorCorrectionLevel: 'Q',
        width: 180,
        margin: 0
      });

      QRCode.toCanvas(checkoutCanvas, this.checkOutUrl, {
        errorCorrectionLevel: 'Q',
        width: 180,
        margin: 0
      });
    });
  }
};
</script>
